/**
 * VersionConflictDialog Component
 * 
 * Shows when trying to save changes that have a version conflict with the server.
 * Allows user to either save anyway (creating a new version) or discard their changes.
 */

import React, { useState } from 'react';
import { AlertTriangle, Save, X, Loader2, GitBranch } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { VersionConflict } from '@/hooks/usePendingChanges';
import { Version } from '@/lib/stores/storeTypes';

interface VersionConflictDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conflicts: VersionConflict[];
  onResolve: (itemId: string, action: 'save' | 'discard') => Promise<void>;
}

export function VersionConflictDialog({
  open,
  onOpenChange,
  conflicts,
  onResolve,
}: VersionConflictDialogProps) {
  const [processingItem, setProcessingItem] = useState<string | null>(null);

  const formatVersion = (version: Version | null): string => {
    if (!version) return 'Unknown';
    
    if (typeof version === 'number') {
      return `v${version}`;
    }
    
    if (typeof version === 'object') {
      // New format: { major, minor, patch }
      if ('major' in version) {
        return `v${(version as any).major}.${(version as any).minor}.${(version as any).patch}`;
      }
      // Old format: { version, changes }
      if ('version' in version && typeof (version as any).version === 'number') {
        return `v${(version as any).version}`;
      }
    }
    
    return 'Unknown';
  };

  const handleResolve = async (itemId: string, action: 'save' | 'discard') => {
    setProcessingItem(itemId);
    try {
      await onResolve(itemId, action);
    } finally {
      setProcessingItem(null);
    }
  };

  if (conflicts.length === 0) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="h-5 w-5" />
            Version Conflict Detected
          </DialogTitle>
          <DialogDescription>
            The following items have been modified on the server since you started editing.
            Choose how to resolve each conflict.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[400px] overflow-y-auto py-4">
          {conflicts.map((conflict) => (
            <div
              key={conflict.itemId}
              className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-900"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-sm">{conflict.itemName}</h4>
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <GitBranch className="h-3 w-3" />
                      <span>Your version: {formatVersion(conflict.localVersion)}</span>
                    </div>
                    <div className="flex items-center gap-1 text-amber-600">
                      <GitBranch className="h-3 w-3" />
                      <span>Server version: {formatVersion(conflict.remoteVersion)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleResolve(conflict.itemId, 'discard')}
                  disabled={processingItem === conflict.itemId}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  {processingItem === conflict.itemId ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <X className="h-3 w-3 mr-1" />
                  )}
                  Discard My Changes
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleResolve(conflict.itemId, 'save')}
                  disabled={processingItem === conflict.itemId}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {processingItem === conflict.itemId ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <Save className="h-3 w-3 mr-1" />
                  )}
                  Save as New Version
                </Button>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default VersionConflictDialog;


















